import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Lock, RadioTower, ShieldAlert, Unlock } from "lucide-react";
import { SignalFeed } from "@/components/signals/SignalEntryCard";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtMYT } from "@/lib/myt";
import { COIN_REFRESH_EVENT } from "@/components/CoinBalanceChip";

const coins = (minor) => `${(minor / 100).toFixed(minor % 100 ? 2 : 0)} coins`;
const COVER_FALLBACK = "/assets/sp-cover-gold.jpg";
const Stat = ({ label, value, tone }) => (
  <div className="flex-1 text-center px-1 min-w-0">
    <p className="text-xs sm:text-sm text-[#A3AEBF] leading-tight">{label}</p>
    <p className={`text-xl font-semibold mt-1.5 ${value == null ? "text-[#9CA3AF]" : tone}`}>{value == null ? "—" : value}</p>
  </div>
);

function RiskNotice() {
  return (
    <div className="sp-notice flex items-start gap-3.5 px-4 py-3.5 sm:px-5 sm:py-4" data-testid="sp-risk-notice">
      <ShieldAlert className="h-7 w-7 sm:h-8 sm:w-8 text-[#E0B84A] shrink-0 mt-0.5" strokeWidth={1.5} />
      <div className="min-w-0">
        <p className="text-sm sm:text-base font-semibold text-[#F0C75E] leading-tight">Risk notice</p>
        <p className="text-sm text-[#CBD5E1] mt-1 leading-snug">Trading involves risk. Signals do not guarantee profits. Past performance does not guarantee future results.</p>
      </div>
    </div>
  );
}

function ProviderCard({ p, onUnlock }) {
  const navigate = useNavigate();
  const a = p.access;
  const pct = p.performance.weekly_pct;
  return (
    <div className="sp-card flex flex-col" data-testid={`sp-card-${p.id}`}>
      <div className="sp-cover">
        <img src={p.image_url || COVER_FALLBACK} alt="" data-testid={`sp-cover-${p.id}`} />
        <div className="absolute inset-x-0 top-0 z-10 flex items-start justify-between gap-3 p-3.5 sm:p-4">
          <div className="flex flex-wrap gap-1.5 min-w-0">{p.pairs.map((x) => <span key={x} className="sp-pill-pair">{x}</span>)}</div>
          <span className={`sp-pill-status shrink-0 ${a.state !== "coming_soon" ? "is-live" : ""}`} data-testid={`sp-status-${p.id}`}>
            {a.state === "coming_soon" ? "COMING SOON" : a.state === "active" ? "ACTIVE" : "LIVE"}
          </span>
        </div>
      </div>
      <div className="px-4 pb-4 sm:px-6 sm:pb-6 -mt-1 flex flex-col flex-1">
        <h2 className="text-white font-bold text-2xl sm:text-[28px] uppercase tracking-wide leading-tight break-words" data-testid={`sp-name-${p.id}`}>{p.name}</h2>
        <p className="text-sm sm:text-base text-[#F0C75E] mt-1">{p.strategy}</p>
        {p.description && <p className="text-sm sm:text-base text-[#D1D5DB] mt-2.5 leading-snug">{p.description}</p>}
        <div className="sp-divider mt-4" />
        <div className="flex items-center justify-between py-3">
          <span className="text-sm sm:text-base text-[#D1D5DB]">Weekly performance</span>
          <span className={`text-lg font-semibold ${pct == null ? "text-[#9CA3AF]" : "text-[#F5C542]"}`} data-testid={`sp-pct-${p.id}`}>{pct == null ? "—" : `${pct}%`}</span>
        </div>
        <div className="sp-divider" />
        <div className="flex divide-x divide-[#2a2a2a] py-3.5">
          <Stat label="Winning pips" value={p.performance.winning_pips} tone="text-emerald-400" />
          <Stat label="Losing pips" value={p.performance.losing_pips} tone="text-red-400" />
          <Stat label="Net pips" value={p.performance.net_pips} tone="text-[#F5C542]" />
        </div>
        <p className="text-sm text-[#8B93A3]" data-testid={`sp-stats-empty-${p.id}`}>{p.performance.period || "No signal data yet"}</p>
        <div className="mt-4 mt-auto pt-4">
          {a.state === "coming_soon" && <button disabled className="sp-action" data-testid={`sp-coming-soon-${p.id}`}><Lock className="h-5 w-5" strokeWidth={1.75} /> Coming Soon</button>}
          {a.state === "locked" && <button onClick={() => onUnlock(p)} className="sp-action" data-testid={`sp-unlock-${p.id}`}><Lock className="h-5 w-5" strokeWidth={1.75} /> Unlock · {coins(p.coin_price_minor)}</button>}
          {a.state === "expired" && <button onClick={() => onUnlock(p)} className="sp-action" data-testid={`sp-unlock-again-${p.id}`}><Lock className="h-5 w-5" strokeWidth={1.75} /> Unlock Again · {coins(p.coin_price_minor)}</button>}
          {a.state === "active" && <button onClick={() => navigate(`/member/signal-providers/${p.id}`)} className="sp-action" data-testid={`sp-open-${p.id}`}><Unlock className="h-5 w-5" strokeWidth={1.75} /> Open Signals</button>}
          <p className="text-sm text-[#A3AEBF] text-center mt-2.5" data-testid={a.state === "active" ? `sp-expiry-${p.id}` : undefined}>
            {a.state === "active" ? `30-day access · until ${fmtMYT(a.expires_at)}` : "30-day access"}
          </p>
        </div>
      </div>
    </div>
  );
}

function ConfirmModal({ p, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [key] = useState(() => `sp:${p.id}:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`);
  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/member/signal-providers/${p.id}/purchase`, { idempotency_key: key });
      window.dispatchEvent(new Event(COIN_REFRESH_EVENT));
      toast.success(`${p.name} unlocked for 30 days`);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="sp-confirm-modal">
      <div className="card-lux w-full sm:max-w-sm p-5 space-y-4 rounded-t-2xl sm:rounded-2xl">
        <h2 className="text-white font-semibold">Unlock {p.name}</h2>
        <div className="text-sm text-[#CBD5E1] space-y-1.5">
          <p className="flex justify-between"><span className="text-[#94A3B8]">Provider</span><span>{p.name}</span></p>
          <p className="flex justify-between"><span className="text-[#94A3B8]">Price</span><span className="text-[#F5C542]" data-testid="sp-confirm-price">{coins(p.coin_price_minor)}</span></p>
          <p className="flex justify-between"><span className="text-[#94A3B8]">Duration</span><span>30 days</span></p>
          <p className="flex justify-between"><span className="text-[#94A3B8]">Renewal</span><span>No automatic renewal</span></p>
        </div>
        <p className="text-[11px] text-[#64748B]">Access starts at the server-confirmed purchase time. Signals before that time are not included.</p>
        <div className="flex gap-2">
          <button onClick={onClose} disabled={busy} className="btn-outline-gold flex-1 py-2.5 text-sm" data-testid="sp-confirm-cancel">Cancel</button>
          <button onClick={confirm} disabled={busy} className="btn-gold flex-1 py-2.5 text-sm disabled:opacity-60" data-testid="sp-confirm-submit">{busy ? "Processing…" : "Confirm"}</button>
        </div>
      </div>
    </div>
  );
}

function Channel({ id }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/member/signal-providers/${id}/channel`).then((r) => setData(r.data)).catch((e) => setErr(formatApiError(e))); }, [id]);
  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="sp-channel-page">
      <button onClick={() => navigate("/member/signal-providers")} className="text-[#94A3B8] hover:text-white flex items-center gap-2 text-sm" data-testid="sp-channel-back"><ArrowLeft className="h-4 w-4" /> Signal Provider</button>
      {err && <div className="card-lux p-6 text-sm text-red-400" data-testid="sp-channel-locked">{err}</div>}
      {data && (
        <div className="sp-card flex flex-col">
          <div className="sp-cover">
            <img src={data.provider.image_url || COVER_FALLBACK} alt="" data-testid="sp-channel-cover" />
            <div className="absolute inset-x-0 top-0 z-10 flex items-start justify-between gap-3 p-3.5 sm:p-4">
              <div className="flex flex-wrap gap-1.5 min-w-0">{data.provider.pairs.map((x) => <span key={x} className="sp-pill-pair">{x}</span>)}</div>
              <span className="sp-pill-status is-live shrink-0" data-testid="sp-channel-status">ACTIVE</span>
            </div>
          </div>
          <div className="px-4 pb-4 sm:px-6 sm:pb-6 -mt-1">
            <h1 className="text-white font-bold text-2xl sm:text-[28px] uppercase tracking-wide leading-tight break-words" data-testid="sp-channel-name">{data.provider.name}</h1>
            <p className="text-sm sm:text-base text-[#F0C75E] mt-1">{data.provider.strategy}</p>
            {data.provider.description && <p className="text-sm sm:text-base text-[#D1D5DB] mt-2.5 leading-snug">{data.provider.description}</p>}
            <div className="sp-divider mt-4" />
            <div className="flex items-center justify-between gap-3 py-3 text-sm sm:text-base">
              <span className="text-[#D1D5DB]">Access window</span>
              <span className="text-[#F0C75E] text-right" data-testid="sp-channel-window">{fmtMYT(data.window.start)} → {fmtMYT(data.window.end)}</span>
            </div>
            <div className="sp-divider" />
            {(data.signals || []).length > 0 ? (
              <div className="pt-4"><SignalFeed signals={data.signals} /></div>
            ) : (
            <div className="py-10 text-center" data-testid="sp-channel-empty">
              <RadioTower className="h-8 w-8 text-[#D4AF37]/50 mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-base font-semibold text-white">No signals yet</p>
              <p className="text-sm text-[#A3AEBF] mt-1.5">Signals published during your access window will appear here.</p>
            </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function MemberSignalProviders() {
  const { id } = useParams();
  const [items, setItems] = useState(null);
  const [unlock, setUnlock] = useState(null);
  const load = () => api.get("/member/signal-providers").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { if (!id) load(); }, [id]);
  if (id) return <Channel id={id} />;
  return (
    <div className={`${items && items.length > 1 ? "max-w-5xl" : "max-w-3xl"} mx-auto space-y-5`} data-testid="signal-providers-page">
      <div><h1 className="page-title">Signal Provider</h1><p className="page-sub mt-1">Explore signal providers</p></div>
      <RiskNotice />
      {items === null ? <p className="text-sm text-[#64748B]">Loading…</p> : items.length === 0 ? (
        <div className="card-lux p-10 text-center" data-testid="sp-empty"><RadioTower className="h-8 w-8 text-[#D4AF37]/40 mx-auto mb-3" /><p className="text-sm text-[#64748B]">No signal providers yet.</p></div>
      ) : (
        <div className={`grid grid-cols-1 gap-5 ${items.length > 1 ? "lg:grid-cols-2" : ""}`}>{items.map((p) => <ProviderCard key={p.id} p={p} onUnlock={setUnlock} />)}</div>
      )}
      {unlock && <ConfirmModal p={unlock} onClose={() => setUnlock(null)} onDone={() => { setUnlock(null); load(); }} />}
    </div>
  );
}
